import React, { forwardRef } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import FormField, {
  cx,
  getControlClassName,
  pickTouched,
} from "components/fields/FormField";

/**
 * Native select with label, hint, error and a chevron.
 *
 * Props:
 * - id (auto when omitted), label, required=false, hint, error, touched: see FormField.
 * - options: [{ value, label, disabled? }] rendered before any `children` <option>s.
 * - placeholder: hidden, disabled empty option shown while value is "".
 * - state: "error" marks the select invalid without a message.
 * - height: "auto" uses vertical padding instead of the fixed h-10.
 * - className: wrapper classes (default "w-full"; pass a width such as "w-20" to size it). labelClassName: extra label classes.
 * - value, onChange, onBlur, name, disabled, style, aria-label, … : forwarded to the <select>.
 * - ref: forwarded to the <select>.
 */
const Select = forwardRef(function Select(props, ref) {
  const {
    id,
    label,
    required = false,
    hint,
    error,
    touched,
    state,
    height,
    placeholder,
    options,
    variant,
    className = "w-full",
    labelClassName,
    children,
    ...rest
  } = props;

  return (
    <FormField
      id={id}
      label={label}
      required={required}
      hint={hint}
      error={error}
      {...pickTouched(props)}
      invalid={state === "error"}
      className={className}
      labelClassName={labelClassName}
    >
      {(controlProps) => (
        <div className="relative">
          <select
            ref={ref}
            required={required}
            {...rest}
            {...controlProps}
            className={getControlClassName({
              invalid: controlProps["aria-invalid"],
              autoHeight: height === "auto",
              className: "cursor-pointer appearance-none pr-9 invalid:text-fg-muted",
            })}
          >
            {placeholder && (
              <option value="" disabled hidden>
                {placeholder}
              </option>
            )}
            {options?.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
            {children}
          </select>
          <MdKeyboardArrowDown
            aria-hidden="true"
            className={cx(
              "pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-fg-muted",
              rest.disabled && "opacity-60"
            )}
          />
        </div>
      )}
    </FormField>
  );
});

export default Select;
